import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import ProvenanceBadge from "@/components/ProvenanceBadge";
import { fmtDateTime, fmtEUR, fmtNum } from "@/lib/format";
import { deriveQuoteCurrency, fmtMoney } from "@/lib/currency";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Save, ChevronDown, ChevronUp, Layers, TrendingUp, TrendingDown, Trash2, AlertTriangle, Link2, Sparkles, Info } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABEL = {
  manual: "Manuell", manual_enriched: "Manuell (angereichert)",
  okx: "OKX", bitget: "Bitget", binance: "Binance",
  kraken: "Kraken", bitunix: "Bitunix", levex: "LeveX",
};

export default function PositionDetailDialog({ position, open, onOpenChange, onUpdated }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFills, setShowFills] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({});
  const [strategies, setStrategies] = useState([]);
  const [showMerge, setShowMerge] = useState(false);
  const [mergeNumber, setMergeNumber] = useState("");
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    if (!open) { setEditing(false); setConfirmDelete(false); setShowMerge(false); setMergeNumber(""); }
    if (position) setForm({
      strategy_name: position.strategy_name || "",
      stop: position.stop ?? "",
      tp1: position.tp1 ?? "",
      tp2: position.tp2 ?? "",
      tp3: position.tp3 ?? "",
      tp1_pct: position.tp1_pct ?? "",
      tp2_pct: position.tp2_pct ?? "",
      tp3_pct: position.tp3_pct ?? "",
      location: position.location || "",
      mood_before: position.mood_before || "",
      mood_after: position.mood_after || "",
      reason: position.reason || "",
      reflection: position.reflection || "",
    });
  }, [open, position]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/strategies`, { withCredentials: true })
      .then((r) => setStrategies(r.data?.strategies || r.data || []))
      .catch(() => setStrategies([]));
  }, [open]);

  if (!position) return null;

  const isOpen = position.is_open;
  const pl = position.total_pl;
  const plColor = pl == null ? "" : pl >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]";
  const primaryProvenance = position.provenances?.[0] || "manual";
  const isMixedSide = (position.sides || []).length > 1;
  // Nur rein manuelle Positionen (geplante Trades, keine API-Daten) sind löschbar.
  const isPureManual = (position.provenances || []).every((p) => p === "manual");
  // Trade-Währung — Priorität:
  //   1) `quote_asset` aus dem Broker-Fill (verlässlich, kommt aus der API).
  //   2) Ableitung aus dem Asset-Symbol als Fallback für manuelle Trades.
  //   3) leer — dann rendert `fmtMoney` nur die Zahl, ohne stille EUR/USDT-
  //      Annahme.
  const tradeCcy = position.quote_asset || deriveQuoteCurrency(position.asset) || "";
  const fmtTrade = (v, opts) => fmtMoney(v, tradeCcy, opts);

  const toNum = (v) => { if (v === "" || v == null) return null; const n = parseFloat(String(v).replace(",", ".")); return Number.isFinite(n) ? n : null; };

  // R-Verhältnis: gewichteter Erwartungswert der Reward/Risk-Ratio über bis zu
  // drei Teil-Exits. WICHTIG: TP2 % bezieht sich auf die verbleibende Position
  // NACH TP1, TP3 % auf die verbleibende Position NACH TP1 + TP2. Beispiel:
  // TP1 50 % → nimmt 50 % der Ursprungs-Position;
  // TP2 50 % → nimmt 50 % vom Rest (0,5) = 25 % der Ursprungs-Position.
  // Reward zählt nur, wenn TP auf der richtigen Trade-Seite liegt.
  const computeExpectedRR = (entry, sl, tps, pcts) => {
    if (entry == null || sl == null || entry === sl) return null;
    const risk = Math.abs(entry - sl);
    const isLong = entry > sl;
    let remaining = 1; // Anteil der Original-Position, der noch offen ist.
    let sumRW = 0;     // Summe(reward * gewicht_der_original_position)
    let usedW = 0;     // Summe der bereits „verbrauchten" Original-Anteile
    for (let i = 0; i < 3; i++) {
      const tp = tps[i];
      let pct = pcts[i];
      if (pct == null || pct <= 0) continue;
      // > 100 % Anteil vom Rest kappen wir auf den Rest.
      if (pct > 100) pct = 100;
      const shareOfRemaining = pct / 100;
      const shareOfOriginal = shareOfRemaining * remaining;
      remaining -= shareOfOriginal;
      if (remaining < 0) remaining = 0;
      if (tp == null) continue;
      const reward = isLong ? (tp - entry) : (entry - tp);
      if (!Number.isFinite(reward) || reward <= 0) continue;
      const r = reward / risk;
      sumRW += r * shareOfOriginal;
      usedW += shareOfOriginal;
    }
    if (usedW <= 0) return null;
    // Auf tatsächlich verkauften Anteil normieren (Rest = „läuft weiter",
    // Reward unbekannt → nicht mitzählen).
    return sumRW / usedW;
  };

  const fmtRR = (rr) => {
    if (rr == null || !Number.isFinite(rr) || rr <= 0) return null;
    // Auf 2 Nachkommastellen runden, Zahlen wie 1.5 → "1,5", 2.0 → "2"
    const rounded = Math.round(rr * 100) / 100;
    const txt = (rounded % 1 === 0)
      ? String(rounded)
      : rounded.toFixed(2).replace(/0+$/, "").replace(/\.$/, "").replace(".", ",");
    return `1 : ${txt}`;
  };

  // SL-Prozent (Risk vom Ø Entry): Long verliert bei sinkendem Kurs, Short bei
  // steigendem. Negativer Wert = Verlust am SL. Wird u. a. in der Facts-Zeile
  // hinter der SL-Zahl in Klammern angezeigt.
  const slPctVsEntry = (() => {
    const stopN = position.stop;
    const entryN = position.avg_entry;
    if (stopN == null || entryN == null || entryN === 0) return null;
    const isLong = (position.primary_side || "long") === "long";
    const moveAbs = ((stopN - entryN) / entryN) * 100;
    return isLong ? moveAbs : -moveAbs;
  })();
  const slPctText = (slPctVsEntry != null)
    ? `${slPctVsEntry > 0 ? "+" : "−"}${Math.abs(slPctVsEntry).toFixed(2).replace(".", ",")}\u00A0%`
    : null;

  const doDelete = async () => {
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/api/trades/positions/${position.id}`, { withCredentials: true });
      toast.success("Position gelöscht", {
        description: `${res.data?.deleted || 0} manueller Eintrag entfernt.`,
      });
      onOpenChange?.(false);
      onUpdated?.();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally { setDeleting(false); }
  };

  const openInCoach = () => {
    if (!position) return;
    const side = (position.sides || []).length > 1 ? "Long+Short" : (position.primary_side === "long" ? "Long" : "Short");
    const state = position.is_open ? "läuft noch" : "geschlossen";
    const parts = [];
    parts.push(`Ich möchte mit dir über meinen Trade ${position.trade_number || ""} sprechen.`.replace("  ", " "));
    parts.push("");
    parts.push(`**Asset:** ${position.asset || "—"} · **Seite:** ${side} · **Status:** ${state}`);
    if (position.avg_entry != null) parts.push(`**Ø Entry:** ${fmtNum(position.avg_entry, 4)}${position.avg_exit != null ? ` · **Ø Exit:** ${fmtNum(position.avg_exit, 4)}` : ""}`);
    if (position.size_total != null) {
      const notional = position.avg_entry != null ? ` (${fmtTrade(position.size_total * position.avg_entry)})` : "";
      parts.push(`**Größe gesamt:** ${fmtNum(position.size_total, 4)}${notional}${position.open_size ? ` · **Offen:** ${fmtNum(position.open_size, 4)}` : ""}`);
    }
    if (position.total_pl != null) parts.push(`**Netto P/L:** ${fmtTrade(position.total_pl, { signed: true })}${position.total_fees ? ` (Gebühren ${fmtTrade(position.total_fees)})` : ""}`);
    if (position.opened_at) parts.push(`**Eröffnet:** ${fmtDateTime(position.opened_at)}${position.closed_at ? ` · **Geschlossen:** ${fmtDateTime(position.closed_at)}` : ""}`);
    if (position.strategy_name) parts.push(`**Strategie:** ${position.strategy_name}`);
    if (position.stop != null) parts.push(`**SL:** ${fmtNum(position.stop, 4)}${position.rr_expected != null ? ` · **R-Verhältnis:** ${fmtRR(position.rr_expected)}` : ""}`);
    if (position.tp1 != null || position.tp2 != null || position.tp3 != null) {
      const tps = [
        position.tp1 != null ? `TP1 ${fmtNum(position.tp1, 4)}${position.tp1_pct != null ? ` (${position.tp1_pct}%)` : ""}` : null,
        position.tp2 != null ? `TP2 ${fmtNum(position.tp2, 4)}${position.tp2_pct != null ? ` (${position.tp2_pct}%)` : ""}` : null,
        position.tp3 != null ? `TP3 ${fmtNum(position.tp3, 4)}${position.tp3_pct != null ? ` (${position.tp3_pct}%)` : ""}` : null,
      ].filter(Boolean).join(" · ");
      if (tps) parts.push(`**Take-Profits:** ${tps}`);
    }
    if (position.location) parts.push(`**Ort:** ${position.location}`);
    if (position.reason) parts.push(`\n**Setup-Notiz:** ${position.reason}`);
    if (position.reflection) parts.push(`\n**Reflexion:** ${position.reflection}`);
    if (position.mood_before || position.mood_after) parts.push(`**Stimmung vorher/nachher:** ${position.mood_before || "—"} / ${position.mood_after || "—"}`);
    parts.push("");
    parts.push(position.is_open
      ? "Was fällt dir auf, worauf sollte ich achten, und welche Frage sollte ich mir jetzt stellen?"
      : "Was fällt dir auf und was kann ich für den nächsten Trade daraus mitnehmen?");
    const payload = {
      position_id: position.id,
      trade_number: position.trade_number || null,
      asset: position.asset || null,
      is_open: !!position.is_open,
      draft: parts.join("\n"),
      created_at: Date.now(),
    };
    try { sessionStorage.setItem("coach_trade_context", JSON.stringify(payload)); } catch { /* still */ }
    onOpenChange?.(false);
    navigate("/app/ki-coach");
  };

  const doLinkByNumber = async () => {
    const num = (mergeNumber || "").trim();
    if (!num) { toast.error("Bitte eine Trade-Nummer eingeben."); return; }
    setMerging(true);
    try {
      const res = await axios.post(
        `${API}/api/trades/positions/${position.id}/link-by-number`,
        { trade_number: num },
        { withCredentials: true },
      );
      toast.success("Trades verknüpft", {
        description: `${res.data.manual_trades_linked} manuelle Fills übernommen · ${res.data.broker_trades_updated} Broker-Fills aktualisiert.`,
      });
      setShowMerge(false);
      setMergeNumber("");
      onOpenChange?.(false);
      onUpdated?.();
    } catch (e) {
      toast.error("Verknüpfen fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally { setMerging(false); }
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const stopN = toNum(form.stop);
      const entryN = position.avg_entry ?? null;
      const tp1N = toNum(form.tp1), tp2N = toNum(form.tp2), tp3N = toNum(form.tp3);
      const p1 = toNum(form.tp1_pct), p2 = toNum(form.tp2_pct), p3 = toNum(form.tp3_pct);
      const rrE = computeExpectedRR(entryN, stopN, [tp1N, tp2N, tp3N], [p1, p2, p3]);
      const payload = {
        strategy_name: form.strategy_name,
        stop: stopN,
        tp1: tp1N, tp2: tp2N, tp3: tp3N,
        tp1_pct: p1, tp2_pct: p2, tp3_pct: p3,
        rr_expected: rrE,
        location: form.location,
        mood_before: form.mood_before,
        mood_after: form.mood_after,
        reason: form.reason,
        reflection: form.reflection,
      };
      const res = await axios.patch(`${API}/api/trades/positions/${position.id}`, payload, { withCredentials: true });
      toast.success("Position aktualisiert", {
        description: `Journal-Notiz auf ${res.data.updated} Fill${res.data.updated === 1 ? "" : "s"} übertragen.`,
      });
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      toast.error("Speichern fehlgeschlagen", { description: e?.response?.data?.detail || "" });
    } finally { setSaving(false); }
  };

  // Live-berechnetes R-Verhältnis: gewichteter Durchschnitt über TP1/TP2/TP3
  // mit den jeweils eingegebenen Prozentanteilen. Fallback: fehlt jede %-Angabe,
  // gleiche Gewichtung; fehlt genau ein TP-Anteil, wird der Rest zum ersten
  // gefüllten TP verschoben.
  const previewRR = (() => {
    const stopN = toNum(form.stop);
    const entryN = position.avg_entry ?? null;
    const tps = [toNum(form.tp1), toNum(form.tp2), toNum(form.tp3)];
    const pcts = [toNum(form.tp1_pct), toNum(form.tp2_pct), toNum(form.tp3_pct)];
    return computeExpectedRR(entryN, stopN, tps, pcts);
  })();
  const pctSum = ["tp1_pct", "tp2_pct", "tp3_pct"]
    .map((k) => toNum(form[k]) || 0)
    .reduce((a, b) => a + b, 0);

  // SL-Preview: prozentuale Kursbewegung vom Ø Entry bis zum SL.
  // Long: SL unter Entry → negative Prozentzahl (Verlust).
  // Short: SL über Entry → wir zeigen ebenfalls den prozentualen Verlust (negativ).
  const slPreview = (() => {
    const stopN = toNum(form.stop);
    const entryN = position.avg_entry ?? null;
    if (stopN == null || entryN == null || entryN === 0) return null;
    const isLong = (position.primary_side || "long") === "long";
    // Kursbewegung (unabhängig von Trade-Richtung):
    const moveAbs = ((stopN - entryN) / entryN) * 100;
    // P/L-relevant: Long verliert bei fallendem Kurs → negativer P/L,
    // Short verliert bei steigendem Kurs → wir spiegeln entsprechend.
    const plPct = isLong ? moveAbs : -moveAbs;
    return { move: moveAbs, plPct };
  })();
  // Passende Strategie (ausgewählt in der Dropdown) zum Plan-Vergleich.
  const selectedStrategy = strategies.find(
    (s) => (s.name || s.id) === (form.strategy_name || "")
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden" data-testid="position-detail-dialog">
        <div className="p-6 md:p-8 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <DialogTitle className="font-heading text-2xl flex items-center gap-2 flex-wrap" data-testid="position-detail-title">
                  <Layers className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                  {position.trade_number && (
                    <span className="font-mono text-sm text-muted-foreground mr-1">{position.trade_number}</span>
                  )}
                  {position.asset}
                  {isMixedSide ? (
                    <Badge variant="secondary" className="rounded-full text-xs">Long + Short</Badge>
                  ) : (
                    <Badge variant="secondary" className="rounded-full capitalize text-xs">
                      {position.primary_side === "long"
                        ? <><TrendingUp className="h-3 w-3 mr-1 inline" strokeWidth={1.5} />Long</>
                        : <><TrendingDown className="h-3 w-3 mr-1 inline" strokeWidth={1.5} />Short</>}
                    </Badge>
                  )}
                  {isOpen && (
                    <Badge className="rounded-full bg-[hsl(43_74%_49%/0.2)] text-[hsl(43_74%_66%)] border border-[hsl(43_74%_49%/0.4)] text-xs">
                      Offen
                    </Badge>
                  )}
                  <ProvenanceBadge
                    source={SOURCE_LABEL[primaryProvenance] || primaryProvenance}
                    label={position.broker_label}
                  />
                </DialogTitle>
                <DialogDescription className="text-muted-foreground mt-1">
                  {position.market || "—"} · {position.fill_count} Transaktion{position.fill_count === 1 ? "" : "en"}
                  {position.broker_label ? ` · ${position.broker_label}` : ""}
                  {(position.refills > 0 || position.partial_exits > 0) && (
                    <span className="ml-1 text-foreground/60">
                      {" · "}
                      {position.refills > 0 && `${position.refills}× Nachkauf`}
                      {position.refills > 0 && position.partial_exits > 0 && " · "}
                      {position.partial_exits > 0 && `${position.partial_exits}× Teilverkauf`}
                    </span>
                  )}
                </DialogDescription>
              </div>
              <div className={`text-right ${plColor}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Netto P/L</div>
                <div className="font-mono text-2xl mt-1" data-testid="position-detail-pl">
                  {pl != null ? fmtTrade(pl, { signed: true }) : "—"}
                </div>
                {position.total_fees > 0 && (
                  <div className="text-[11px] text-muted-foreground mt-1 font-mono">
                    Gebühren: {fmtTrade(position.total_fees)}
                  </div>
                )}
              </div>
            </div>
          </DialogHeader>

          <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="position-detail-facts">
            <Fact label="Ø Entry" value={position.avg_entry != null ? fmtNum(position.avg_entry, 4) : "—"} />
            <Fact label="Ø Exit" value={position.avg_exit != null ? fmtNum(position.avg_exit, 4) : "—"} />
            <Fact label="Größe gesamt" value={
              position.avg_entry != null && position.size_total != null
                ? `${fmtNum(position.size_total, 4)} (${fmtTrade(position.size_total * position.avg_entry)})`
                : fmtNum(position.size_total, 4)
            } />
            <Fact label="Offen / Zu" value={`${fmtNum(position.open_size, 2)} / ${fmtNum(position.closed_size, 2)}`} />
            <Fact label="Eröffnet" value={position.opened_at ? fmtDateTime(position.opened_at) : "—"} />
            <Fact label="Geschlossen" value={position.closed_at ? fmtDateTime(position.closed_at) : "—"} />
            <Fact label="SL" value={position.stop != null ? (slPctText ? `${fmtNum(position.stop, 4)} (${slPctText})` : fmtNum(position.stop, 4)) : "—"} />
            <Fact label="R-Verhältnis" value={fmtRR(position.rr_expected) || "—"} />
          </section>

          {/* Journal-Notiz für die gesamte Position */}
          <section className="mt-8" data-testid="position-detail-journal">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Journal · gilt für die gesamte Position</div>
              {!editing && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant="outline" size="sm" className="rounded-full h-8" onClick={openInCoach} data-testid="position-detail-coach">
                    <Sparkles className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} /> Mit KI-Coach besprechen
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-full h-8" onClick={() => setEditing(true)} data-testid="position-detail-edit">
                    <Pencil className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} /> Bearbeiten
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full h-8"
                    onClick={() => setShowMerge((v) => !v)}
                    data-testid="position-detail-link-toggle"
                  >
                    <Link2 className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} /> Mit Trade-Nummer verknüpfen
                  </Button>
                  {isPureManual && !confirmDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full h-8 text-[hsl(0_66%_72%)] hover:text-[hsl(0_66%_72%)] hover:bg-[hsl(0_66%_50%/0.1)]"
                      onClick={() => setConfirmDelete(true)}
                      data-testid="position-detail-delete"
                    >
                      <Trash2 className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} /> Löschen
                    </Button>
                  )}
                  {isPureManual && confirmDelete && (
                    <div className="flex items-center gap-2 rounded-full bg-[hsl(0_66%_50%/0.08)] border border-[hsl(0_66%_50%/0.3)] pl-3 pr-1 py-1" data-testid="position-detail-delete-confirm-row">
                      <AlertTriangle className="h-3.5 w-3.5 text-[hsl(0_66%_72%)]" strokeWidth={1.75} />
                      <span className="text-xs text-foreground/80">Wirklich löschen? Nur geplant, nie getradet.</span>
                      <Button variant="ghost" size="sm" className="rounded-full h-7 px-3 text-xs" onClick={() => setConfirmDelete(false)} disabled={deleting} data-testid="position-detail-delete-cancel">Abbrechen</Button>
                      <Button size="sm" className="rounded-full h-7 px-3 text-xs bg-[hsl(0_66%_50%)] hover:bg-[hsl(0_66%_44%)] text-white" onClick={doDelete} disabled={deleting} data-testid="position-detail-delete-yes">
                        {deleting ? "Löscht…" : "Ja, löschen"}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
            {showMerge && !editing && (
              <div className="mb-4 rounded-xl border border-[hsl(38_92%_50%/0.4)] bg-[hsl(38_92%_50%/0.06)] p-4" data-testid="position-detail-merge-panel">
                <div className="text-sm font-medium mb-1">Manuellen Trade mit Broker-Trade verknüpfen</div>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                  Gib die Trade-Nummer der jeweils anderen Position ein (z. B. <span className="font-mono">2026_09_5</span>).
                  Die Notizen des manuellen Trades werden auf die Broker-Position übertragen, die manuellen Fills verschwinden aus dem Journal.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    value={mergeNumber}
                    onChange={(e) => setMergeNumber(e.target.value)}
                    placeholder="Trade-Nummer, z. B. 2026_09_5"
                    className="max-w-xs font-mono"
                    data-testid="position-detail-merge-number"
                    disabled={merging}
                  />
                  <Button size="sm" className="rounded-full" onClick={doLinkByNumber} disabled={merging || !mergeNumber.trim()} data-testid="position-detail-merge-submit">
                    <Link2 className="mr-2 h-3.5 w-3.5" strokeWidth={1.5} /> {merging ? "Verknüpfe…" : "Verknüpfen"}
                  </Button>
                  <Button variant="ghost" size="sm" className="rounded-full" onClick={() => { setShowMerge(false); setMergeNumber(""); }} disabled={merging} data-testid="position-detail-merge-cancel">
                    Abbrechen
                  </Button>
                </div>
              </div>
            )}
            {editing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <FieldE label="Strategie">
                    <Select value={form.strategy_name || "__none"} onValueChange={(v) => setForm((s) => ({ ...s, strategy_name: v === "__none" ? "" : v }))}>
                      <SelectTrigger data-testid="position-edit-strategy"><SelectValue placeholder="Strategie wählen …" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">Keine Strategie</SelectItem>
                        {strategies.map((s) => (
                          <SelectItem key={s.id || s.name} value={s.name || s.id}>{s.name || s.id}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldE>
                  <FieldE label="Ort (z. B. Büro, Hotel, Home-Office)"><Input value={form.location} onChange={(e) => setForm((s) => ({ ...s, location: e.target.value }))} placeholder="Ort" data-testid="position-edit-location" /></FieldE>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <FieldE label="SL (Stop-Loss)"><Input value={form.stop} onChange={(e) => setForm((s) => ({ ...s, stop: e.target.value }))} placeholder="Preis" data-testid="position-edit-stop" inputMode="decimal" className="font-mono" /></FieldE>
                  <FieldE label="TP1"><Input value={form.tp1} onChange={(e) => setForm((s) => ({ ...s, tp1: e.target.value }))} placeholder="Preis" data-testid="position-edit-tp1" inputMode="decimal" className="font-mono" /></FieldE>
                  <FieldE label="TP2"><Input value={form.tp2} onChange={(e) => setForm((s) => ({ ...s, tp2: e.target.value }))} placeholder="Preis" data-testid="position-edit-tp2" inputMode="decimal" className="font-mono" /></FieldE>
                  <FieldE label="TP3"><Input value={form.tp3} onChange={(e) => setForm((s) => ({ ...s, tp3: e.target.value }))} placeholder="Preis" data-testid="position-edit-tp3" inputMode="decimal" className="font-mono" /></FieldE>
                </div>
                {slPreview && (
                  <div className="rounded-lg border border-border bg-secondary/20 p-3" data-testid="position-edit-sl-preview">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Wenn im SL geschlossen wird</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Kurs-Bewegung Entry → SL: <span className="font-mono">{`${slPreview.move > 0 ? "+" : ""}${slPreview.move.toFixed(2).replace(".", ",")}\u00A0%`}</span>
                        </div>
                      </div>
                      <div className={`font-mono text-lg ${slPreview.plPct < 0 ? "text-[hsl(0_66%_72%)]" : "text-[hsl(122_39%_65%)]"}`} data-testid="position-edit-sl-pct">
                        {`${slPreview.plPct > 0 ? "+" : "−"}${Math.abs(slPreview.plPct).toFixed(2).replace(".", ",")}\u00A0%`}
                      </div>
                    </div>
                    {selectedStrategy?.sl_description ? (
                      <div className="mt-2 pt-2 border-t border-border/60 text-xs">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">SL-Plan laut Strategie „{selectedStrategy.name}"</div>
                        <div className="text-foreground/80 leading-relaxed whitespace-pre-wrap">{selectedStrategy.sl_description}</div>
                      </div>
                    ) : (form.strategy_name && !selectedStrategy?.sl_description) ? (
                      <div className="mt-2 pt-2 border-t border-border/60 text-[11px] text-muted-foreground">
                        Für diese Strategie ist noch keine SL-Beschreibung hinterlegt. In den Strategie-Details ergänzen, damit wir hier abgleichen können.
                      </div>
                    ) : null}
                  </div>
                )}
                <div className="rounded-lg border border-[hsl(38_92%_50%/0.35)] bg-[hsl(38_92%_50%/0.06)] p-3 flex items-start gap-2 text-xs" data-testid="position-edit-tp-pct-hint">
                  <Info className="h-4 w-4 text-[hsl(38_92%_66%)] shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <div className="font-medium text-foreground">TP-Prozente beziehen sich auf die jeweils verbleibende Position</div>
                    <p className="text-muted-foreground mt-0.5 leading-relaxed">
                      Beispiel: TP1 50 % schließt 50 % der Ursprungs-Position. TP2 50 % nimmt dann 50 % vom Rest — also 25 % der ursprünglichen Position. Der offene Restanteil läuft weiter und fließt nicht ins R-Verhältnis ein.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-end pb-2">Anteil je TP (% vom Rest)</div>
                  <FieldE label="TP1 % (vom Original)"><Input value={form.tp1_pct} onChange={(e) => setForm((s) => ({ ...s, tp1_pct: e.target.value }))} placeholder="z. B. 50" data-testid="position-edit-tp1-pct" inputMode="decimal" className="font-mono" /></FieldE>
                  <FieldE label="TP2 % (vom Rest)"><Input value={form.tp2_pct} onChange={(e) => setForm((s) => ({ ...s, tp2_pct: e.target.value }))} placeholder="z. B. 50" data-testid="position-edit-tp2-pct" inputMode="decimal" className="font-mono" /></FieldE>
                  <FieldE label="TP3 % (vom Rest)"><Input value={form.tp3_pct} onChange={(e) => setForm((s) => ({ ...s, tp3_pct: e.target.value }))} placeholder="z. B. 100" data-testid="position-edit-tp3-pct" inputMode="decimal" className="font-mono" /></FieldE>
                </div>
                <div className="rounded-lg border border-border bg-secondary/20 p-3 flex items-center justify-between flex-wrap gap-2" data-testid="position-edit-rr-preview">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Erwartetes R-Verhältnis (Risk : Reward)</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Gewichtet über die tatsächlich abgeschlossenen Anteile (TP1 vom Original · TP2/TP3 vom jeweils Verbleibenden).
                    </div>
                  </div>
                  <div className="font-mono text-lg">
                    {previewRR != null ? fmtRR(previewRR) : <span className="text-muted-foreground text-sm">— SL, Entry & mind. 1 TP mit Anteil pflegen</span>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FieldE label="Stimmung vorher"><Input value={form.mood_before} onChange={(e) => setForm((s) => ({ ...s, mood_before: e.target.value }))} placeholder="z. B. fokussiert" data-testid="position-edit-mood-before" /></FieldE>
                  <FieldE label="Stimmung nachher"><Input value={form.mood_after} onChange={(e) => setForm((s) => ({ ...s, mood_after: e.target.value }))} placeholder="z. B. zufrieden" data-testid="position-edit-mood-after" /></FieldE>
                </div>
                <FieldE label="Setup-Notiz"><Textarea rows={2} value={form.reason} onChange={(e) => setForm((s) => ({ ...s, reason: e.target.value }))} data-testid="position-edit-reason" /></FieldE>
                <FieldE label="Reflexion / Lernen"><Textarea rows={3} value={form.reflection} onChange={(e) => setForm((s) => ({ ...s, reflection: e.target.value }))} data-testid="position-edit-reflection" /></FieldE>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setEditing(false)} disabled={saving} data-testid="position-edit-cancel">Abbrechen</Button>
                  <Button size="sm" className="rounded-full" onClick={doSave} disabled={saving} data-testid="position-edit-save">
                    <Save className="mr-2 h-4 w-4" strokeWidth={1.5} /> {saving ? "Speichert…" : "Für alle Fills speichern"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {position.strategy_name || position.reason || position.reflection || position.mood_before || position.mood_after || position.location || position.tp1 != null || position.tp2 != null || position.tp3 != null ? (
                  <>
                    {position.strategy_name && <Block label="Strategie" value={position.strategy_name} />}
                    {position.location && <Block label="Ort" value={position.location} />}
                    {(position.tp1 != null || position.tp2 != null || position.tp3 != null || position.stop != null || position.rr_expected != null) && (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {position.stop != null && <Fact label="SL" value={slPctText ? `${fmtNum(position.stop, 4)} (${slPctText})` : fmtNum(position.stop, 4)} />}
                        {position.tp1 != null && <Fact label={position.tp1_pct != null ? `TP1 (${position.tp1_pct}%)` : "TP1"} value={fmtNum(position.tp1, 4)} />}
                        {position.tp2 != null && <Fact label={position.tp2_pct != null ? `TP2 (${position.tp2_pct}%)` : "TP2"} value={fmtNum(position.tp2, 4)} />}
                        {position.tp3 != null && <Fact label={position.tp3_pct != null ? `TP3 (${position.tp3_pct}%)` : "TP3"} value={fmtNum(position.tp3, 4)} />}
                        {position.rr_expected != null && <Fact label="R-Verhältnis" value={fmtRR(position.rr_expected) || "—"} />}
                      </div>
                    )}
                    {position.reason && <Block label="Setup-Notiz" value={position.reason} />}
                    {position.reflection && <Block label="Reflexion / Lernen" value={position.reflection} />}
                    {(position.mood_before || position.mood_after) && (
                      <div className="grid grid-cols-2 gap-3">
                        {position.mood_before && <Fact label="Stimmung vorher" value={position.mood_before} />}
                        {position.mood_after && <Fact label="Stimmung nachher" value={position.mood_after} />}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="position-detail-journal-empty">
                    Noch keine Notiz für diese Position. Über „Bearbeiten" hinzufügen — die Notiz gilt dann für alle {position.fill_count} Fills.
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Fill-Liste */}
          <section className="mt-8" data-testid="position-detail-fills">
            <button
              type="button"
              onClick={() => setShowFills((s) => !s)}
              className="flex items-center gap-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors mb-3"
              data-testid="position-detail-fills-toggle"
            >
              {showFills ? <ChevronUp className="h-4 w-4" strokeWidth={1.5} /> : <ChevronDown className="h-4 w-4" strokeWidth={1.5} />}
              {position.fill_count} Transaktion{position.fill_count === 1 ? "" : "en"} anzeigen
            </button>
            {showFills && (
              <div className="rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/40">
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2">Zeitpunkt</th>
                      <th className="px-3 py-2">Seite</th>
                      <th className="px-3 py-2 text-right">Entry</th>
                      <th className="px-3 py-2 text-right">Exit</th>
                      <th className="px-3 py-2 text-right">Größe</th>
                      <th className="px-3 py-2 text-right">P/L</th>
                      <th className="px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {position.fills.map((f) => (
                      <tr key={f.id} className="border-t border-border hover:bg-secondary/20" data-testid={`position-fill-row-${f.id}`}>
                        <td className="px-3 py-2 text-muted-foreground text-xs font-mono">
                          {f.opened_at ? fmtDateTime(f.opened_at) : "—"}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${f.side === "long" ? "bg-[hsl(122_39%_49%/0.15)] text-[hsl(122_39%_65%)]" : "bg-[hsl(0_66%_50%/0.15)] text-[hsl(0_66%_72%)]"}`}>
                            {f.side === "long" ? <TrendingUp className="h-3 w-3" strokeWidth={1.5} /> : <TrendingDown className="h-3 w-3" strokeWidth={1.5} />}
                            {f.side}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{f.entry != null ? fmtNum(f.entry, 4) : "—"}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{f.exit != null ? fmtNum(f.exit, 4) : "—"}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">{f.size != null ? fmtNum(f.size, 4) : "—"}</td>
                        <td className={`px-3 py-2 text-right font-mono text-xs ${(f.pl || 0) >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]"}`}>
                          {f.pl != null ? fmtEUR(f.pl, { signed: true }) : "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground capitalize">{f.status || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Fact({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-sm break-all">{value}</div>
    </div>
  );
}

function Block({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm whitespace-pre-wrap">{value}</div>
    </div>
  );
}

function FieldE({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
